import type { BrandCode } from './constants';
import { getBrandCode } from './brandHelper';

/**
 * Brand detection and context for multi-domain support.
 * 
 * Contains only the brand code. Brand-specific logic (OAuth endpoints, routes,
 * AEM params) is resolved at the point of use rather than stored in context.
 */
export interface IBrandContext {
  brandCode: BrandCode;
}

/**
 * Synchronous accessor for the resolved brand context. Safe to call from
 * non-React code (entry script, plain utilities, tests).
 *
 * Memoized once per page load — brand cannot change without a full
 * navigation, so re-evaluating is wasted work and risks divergence between
 * callers.
 */
let cachedContext: IBrandContext | null = null;

export const setBrandContext = (brandCode: BrandCode | null | undefined): void => {
  cachedContext = brandCode ? { brandCode } : null;
};

export const getBrandContext = (): IBrandContext => {
  if (cachedContext) {
    return cachedContext;
  }
  cachedContext = {
    brandCode: getBrandCode(),
  };
  return cachedContext;
};

/**
 * Test-only helper to clear the memoized context. Not exported from the
 * barrel — import directly from this file in tests if needed.
 */
export const __resetBrandContextForTests = (): void => {
  cachedContext = null;
};
